import React, { useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft, Plus, Search, Copy, Trash2, Eye, EyeOff,
  Library, Pencil,
} from "lucide-react";
import {
  CATEGORIES, CATEGORY_LABELS, CATEGORY_TAGLINES, SOURCE_LABELS,
} from "@/lib/reflibs/schema";
import { listByCategory, getEffectiveCounts } from "@/lib/reflibs/lookup";
import {
  cloneAsUserRecord, deleteUserRecord, setUserRecordActive,
} from "@/lib/reflibs/storage";
import {
  getLibraryReturnCtx, forwardLibraryReturn, restoreDraftState,
} from "@/lib/reflibs/returnContext";

// Tab strip
function _rowLabel(r) {
  // Best-effort short label per category.
  if (r.stackName) return r.stackName;                              // bop
  if (r.pumpModel) return `${r.pumpModel}${r.manufacturer ? " · " + r.manufacturer : ""}`;
  if (r.model)     return `${r.model}${r.manufacturer ? " · " + r.manufacturer : ""}`;
  if (r.nominalSize || r.grade || r.connection)
    return [r.nominalSize, r.grade, r.connection].filter(Boolean).join(" · ");
  return r.id;
}
function CategoryTabs({ active, counts, onPick }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto" data-testid="reflibs-tabs">
      {CATEGORIES.map((c) => {
        const isActive = c === active;
        return (
          <button key={c}
                  type="button"
                  data-testid={`reflibs-tab-${c}`}
                  onClick={() => onPick(c)}
                  className="px-3 py-2 rounded-md text-[11.5px] font-medium whitespace-nowrap"
                  style={{
                    background: isActive ? "var(--rs-amber)" : "transparent",
                    color: isActive ? "#0A0B0E" : "var(--rs-text-2)",
                    border: `1px solid ${isActive ? "var(--rs-amber)" : "var(--rs-border)"}`,
                    minHeight: 36,
                  }}>
            {CATEGORY_LABELS[c]}
            <span className="ml-1.5 font-mono text-[9.5px] opacity-70">
              {counts[c] ?? 0}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function ReferenceLibrariesHub() {
  const navigate = useNavigate();
  const location = useLocation();
  const { category: routeCat } = useParams();
  const initialCat = CATEGORIES.includes(routeCat) ? routeCat : "drill_pipe";
  const [category, setCategory] = useState(initialCat);
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  // Bumped after every delete / active-toggle mutation. `setQ((x) => x)` and
  // `setCategory((c) => c)` are React state bail-outs (identical value —
  // no re-render), so the table never reflected a delete/hide until a full
  // page reload. This counter is a real state change every time.
  const [dataVersion, setDataVersion] = useState(0);
  const returnCtx = getLibraryReturnCtx(location);

  const counts = useMemo(() => getEffectiveCounts(), [category, dataVersion]);
  const rows = useMemo(() => {
    const all = listByCategory(category, { includeInactive: showInactive });
    if (!q.trim()) return all;
    const qLow = q.toLowerCase();
    return all.filter((r) => {
      // Cover every common field across all 14 categories.
      const hay = [
        r.nominalSize, r.grade, r.connection, r.id, r.notes,
        r.model, r.manufacturer, r.stackName, r.pumpModel,
        r.bopType, r.bitType, r.pumpType, r.bladeType, r.steeringMode, r.telemetryType,
        r.iadcCode, r.topConnection, r.bottomConnection, r.sensors, r.lobes,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(qLow);
    });
  }, [category, q, showInactive, dataVersion]);

  const handleSwitch = (c) => {
    setCategory(c);
    setQ("");
    navigate(`/reference-libraries/${c}`, { replace: true, state: forwardLibraryReturn(location) });
  };

  const handleBack = () => {
    if (returnCtx) navigate(returnCtx.from, { state: restoreDraftState(returnCtx) });
    else navigate("/home");
  };

  const handleClone = (row) => {
    try {
      const copy = cloneAsUserRecord(row);
      navigate(`/reference-libraries/${category}/${copy.id}`, { state: forwardLibraryReturn(location) });
    } catch (e) {
      alert(e.message || "Clone failed");
    }
  };
  const handleDeleteOrToggle = (row) => {
    if (row.source === "rsc_seed_v1") {
      alert("Seed records cannot be deleted. Clone it to create an editable copy.");
      return;
    }
    if (!window.confirm(`Delete "${_rowLabel(row)}"?`)) return;
    deleteUserRecord(row.id);
    setDataVersion((v) => v + 1);
  };
  const handleToggleActive = (row) => {
    if (row.source === "rsc_seed_v1") {
      alert("Seed records are always active. Clone it to hide a copy from pickers.");
      return;
    }
    setUserRecordActive(row.id, !(row.isActive !== false));
    setDataVersion((v) => v + 1);
  };

  return (
    <div data-testid="reflibs-hub" className="flex flex-col gap-4 pb-12">
      <header className="flex items-center justify-between gap-3">
        <button type="button" data-testid="reflibs-back"
                onClick={handleBack}
                className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]">
          <ArrowLeft className="w-3.5 h-3.5" /> {returnCtx ? "Back" : "Home"}
        </button>
        <button type="button" data-testid="reflibs-new"
                onClick={() => navigate(`/reference-libraries/${category}/new`, { state: forwardLibraryReturn(location) })}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium"
                style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 36 }}>
          <Plus className="w-4 h-4" /> New {CATEGORY_LABELS[category]} Record
        </button>
      </header>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--rs-amber)]">
          Kill Sheet v2 · Module 2
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl tracking-tight mt-1">Reference Libraries</h1>
        <p className="text-[12px] text-[var(--rs-text-2)] mt-1 leading-snug">
          Engineering catalog for tubulars. Select a spec once → kill sheets,
          well profiles, and string builders auto-populate OD, ID, weight,
          grade, connection, and derived capacities.
        </p>
        <p className="text-[10.5px] text-[var(--rs-text-3)] mt-1 leading-snug">
          Seed dataset is read-only. Cloning a seed row creates an editable
          user copy. Numeric units: <span className="font-mono">in</span> ·
          <span className="font-mono"> lb/ft</span> ·
          <span className="font-mono"> bbl/ft</span>.
        </p>
      </div>

      <CategoryTabs active={category} counts={counts} onPick={handleSwitch} />

      <div className="card-rs p-3">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <div className="flex items-center gap-1.5 flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 text-[var(--rs-text-3)]" />
            <input
              data-testid="reflibs-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter: size / grade / connection / id"
              className="input-rs flex-1 text-[12px]"
              style={{ minHeight: 32 }}
            />
          </div>
          <label className="inline-flex items-center gap-1.5 text-[11px] text-[var(--rs-text-2)] cursor-pointer">
            <input type="checkbox" data-testid="reflibs-show-inactive"
                   checked={showInactive}
                   onChange={(e) => setShowInactive(e.target.checked)} />
            Show inactive
          </label>
        </div>

        <p className="text-[10.5px] text-[var(--rs-text-3)] mb-2 leading-snug">
          {CATEGORY_TAGLINES[category]}
        </p>

        {rows.length === 0 ? (
          <div data-testid="reflibs-empty" className="text-center py-6 text-[11.5px] text-[var(--rs-text-3)]">
            No records match the current filter.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="reflibs-table">
              <thead>
                <tr className="text-left text-[9.5px] uppercase tracking-[0.18em] text-[var(--rs-text-3)] font-mono">
                  {category === "bop" ? (
                    <>
                      <th className="py-1.5 pr-2">Stack</th>
                      <th className="py-1.5 pr-2">Type</th>
                      <th className="py-1.5 pr-2">Size</th>
                      <th className="py-1.5 pr-2 text-right">WP (psi)</th>
                      <th className="py-1.5 pr-2 text-right">Rams</th>
                      <th className="py-1.5 pr-2 text-right">Choke ID</th>
                      <th className="py-1.5 pr-2 text-right">Kill ID</th>
                      <th className="py-1.5 pr-2">Src</th>
                      <th className="py-1.5"></th>
                    </>
                  ) : category === "bit" ? (
                    <>
                      <th className="py-1.5 pr-2 text-right">Size (in)</th>
                      <th className="py-1.5 pr-2">Type</th>
                      <th className="py-1.5 pr-2">IADC</th>
                      <th className="py-1.5 pr-2">Manufacturer</th>
                      <th className="py-1.5 pr-2">Model</th>
                      <th className="py-1.5 pr-2 text-right">Nozzles</th>
                      <th className="py-1.5 pr-2 text-right">TFA</th>
                      <th className="py-1.5 pr-2">Src</th>
                      <th className="py-1.5"></th>
                    </>
                  ) : category === "pump" ? (
                    <>
                      <th className="py-1.5 pr-2">Model</th>
                      <th className="py-1.5 pr-2">Mfr</th>
                      <th className="py-1.5 pr-2">Type</th>
                      <th className="py-1.5 pr-2 text-right">Liner</th>
                      <th className="py-1.5 pr-2 text-right">Stroke</th>
                      <th className="py-1.5 pr-2 text-right">Eff %</th>
                      <th className="py-1.5 pr-2 text-right">Max P</th>
                      <th className="py-1.5 pr-2">Src</th>
                      <th className="py-1.5"></th>
                    </>
                  ) : category === "mud_motor" ? (
                    <>
                      <th className="py-1.5 pr-2">Model</th>
                      <th className="py-1.5 pr-2 text-right">OD</th>
                      <th className="py-1.5 pr-2">Lobes</th>
                      <th className="py-1.5 pr-2 text-right">Bend °</th>
                      <th className="py-1.5 pr-2 text-right">Max Q</th>
                      <th className="py-1.5 pr-2 text-right">Max RPM</th>
                      <th className="py-1.5 pr-2">Conn</th>
                      <th className="py-1.5 pr-2">Src</th>
                      <th className="py-1.5"></th>
                    </>
                  ) : category === "rotary_steerable" ? (
                    <>
                      <th className="py-1.5 pr-2">Model</th>
                      <th className="py-1.5 pr-2 text-right">OD</th>
                      <th className="py-1.5 pr-2">Mode</th>
                      <th className="py-1.5 pr-2 text-right">Max DLS</th>
                      <th className="py-1.5 pr-2 text-right">Max Q</th>
                      <th className="py-1.5 pr-2 text-right">Max RPM</th>
                      <th className="py-1.5 pr-2">Conn</th>
                      <th className="py-1.5 pr-2">Src</th>
                      <th className="py-1.5"></th>
                    </>
                  ) : category === "mwd_tool" ? (
                    <>
                      <th className="py-1.5 pr-2">Model</th>
                      <th className="py-1.5 pr-2 text-right">OD</th>
                      <th className="py-1.5 pr-2 text-right">ID</th>
                      <th className="py-1.5 pr-2">Telem</th>
                      <th className="py-1.5 pr-2 text-right">Max Q</th>
                      <th className="py-1.5 pr-2 text-right">Max P</th>
                      <th className="py-1.5 pr-2">Conn</th>
                      <th className="py-1.5 pr-2">Src</th>
                      <th className="py-1.5"></th>
                    </>
                  ) : category === "lwd_tool" ? (
                    <>
                      <th className="py-1.5 pr-2">Model</th>
                      <th className="py-1.5 pr-2 text-right">OD</th>
                      <th className="py-1.5 pr-2 text-right">ID</th>
                      <th className="py-1.5 pr-2">Sensors</th>
                      <th className="py-1.5 pr-2 text-right">Max Q</th>
                      <th className="py-1.5 pr-2 text-right">Max P</th>
                      <th className="py-1.5 pr-2">Conn</th>
                      <th className="py-1.5 pr-2">Src</th>
                      <th className="py-1.5"></th>
                    </>
                  ) : category === "stabilizer" ? (
                    <>
                      <th className="py-1.5 pr-2">Name</th>
                      <th className="py-1.5 pr-2 text-right">Gauge OD</th>
                      <th className="py-1.5 pr-2">Blade</th>
                      <th className="py-1.5 pr-2 text-right">Blades</th>
                      <th className="py-1.5 pr-2 text-right">Thru ID</th>
                      <th className="py-1.5 pr-2">Conn</th>
                      <th className="py-1.5 pr-2">Src</th>
                      <th className="py-1.5"></th>
                    </>
                  ) : category === "crossover" ? (
                    <>
                      <th className="py-1.5 pr-2">Name</th>
                      <th className="py-1.5 pr-2">Top</th>
                      <th className="py-1.5 pr-2">Bottom</th>
                      <th className="py-1.5 pr-2 text-right">OD</th>
                      <th className="py-1.5 pr-2 text-right">ID</th>
                      <th className="py-1.5 pr-2 text-right">Len (in)</th>
                      <th className="py-1.5 pr-2">Src</th>
                      <th className="py-1.5"></th>
                    </>
                  ) : (
                    <>
                      <th className="py-1.5 pr-2">Size</th>
                      <th className="py-1.5 pr-2">Weight</th>
                      <th className="py-1.5 pr-2">Grade</th>
                      <th className="py-1.5 pr-2">Connection</th>
                      <th className="py-1.5 pr-2 text-right">OD (in)</th>
                      <th className="py-1.5 pr-2 text-right">ID (in)</th>
                      <th className="py-1.5 pr-2 text-right">Cap (bbl/ft)</th>
                      <th className="py-1.5 pr-2 text-right">Disp (bbl/ft)</th>
                      <th className="py-1.5 pr-2">Src</th>
                      <th className="py-1.5"></th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const inactive = r.isActive === false;
                  return (
                    <tr key={r.id}
                        data-testid={`reflibs-row-${r.id}`}
                        className="border-t border-[var(--rs-border)] hover:bg-[rgba(245,180,0,0.04)]"
                        style={inactive ? { opacity: 0.45 } : undefined}>
                      {category === "bop" ? (
                        <>
                          <td className="py-1.5 pr-2">{r.stackName}</td>
                          <td className="py-1.5 pr-2 capitalize">{r.bopType}</td>
                          <td className="py-1.5 pr-2">{r.nominalSize}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.workingPressure}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.ramSlots}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.chokeLineId || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.killLineId || "—"}</td>
                        </>
                      ) : category === "bit" ? (
                        <>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.bitSize}</td>
                          <td className="py-1.5 pr-2">{r.bitType}</td>
                          <td className="py-1.5 pr-2 font-mono">{r.iadcCode || "—"}</td>
                          <td className="py-1.5 pr-2">{r.manufacturer || "—"}</td>
                          <td className="py-1.5 pr-2">{r.model || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.nozzles || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.tfa || "—"}</td>
                        </>
                      ) : category === "pump" ? (
                        <>
                          <td className="py-1.5 pr-2">{r.pumpModel}</td>
                          <td className="py-1.5 pr-2">{r.manufacturer || "—"}</td>
                          <td className="py-1.5 pr-2 capitalize">{r.pumpType}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.linerSize}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.strokeLength}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.efficiency}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.maxPressurePsi || "—"}</td>
                        </>
                      ) : category === "mud_motor" ? (
                        <>
                          <td className="py-1.5 pr-2">{r.model}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.outerDiameter || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono">{r.lobes || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.bendDegrees ?? "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.maxFlowGpm || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.maxRpm || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono">{r.connection || "—"}</td>
                        </>
                      ) : category === "rotary_steerable" ? (
                        <>
                          <td className="py-1.5 pr-2">{r.model}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.outerDiameter || "—"}</td>
                          <td className="py-1.5 pr-2">{r.steeringMode || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.maxDoglegDeg100ft || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.maxFlowGpm || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.maxRpm || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono">{r.connection || "—"}</td>
                        </>
                      ) : category === "mwd_tool" ? (
                        <>
                          <td className="py-1.5 pr-2">{r.model}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.outerDiameter || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.innerDiameter || "—"}</td>
                          <td className="py-1.5 pr-2">{r.telemetryType || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.maxFlowGpm || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.maxPressurePsi || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono">{r.connection || "—"}</td>
                        </>
                      ) : category === "lwd_tool" ? (
                        <>
                          <td className="py-1.5 pr-2">{r.model}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.outerDiameter || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.innerDiameter || "—"}</td>
                          <td className="py-1.5 pr-2 truncate max-w-[140px]" title={r.sensors}>{r.sensors || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.maxFlowGpm || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.maxPressurePsi || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono">{r.connection || "—"}</td>
                        </>
                      ) : category === "stabilizer" ? (
                        <>
                          <td className="py-1.5 pr-2">{r.model}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.outerDiameter || "—"}</td>
                          <td className="py-1.5 pr-2">{r.bladeType || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.blades || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.throughPipeId || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono">{r.connection || "—"}</td>
                        </>
                      ) : category === "crossover" ? (
                        <>
                          <td className="py-1.5 pr-2">
                            {r.model}
                            {r.stepped && (
                              <span
                                data-testid={`reflibs-xo-stepped-${r.id}`}
                                className="ml-1.5 text-[8.5px] uppercase tracking-[0.18em] px-1 py-0.5 rounded border"
                                style={{ borderColor: "var(--rs-amber, #f5b400)", color: "var(--rs-amber, #f5b400)" }}
                                title="Stepped (two-OD) crossover"
                              >
                                Stepped
                              </span>
                            )}
                          </td>
                          <td className="py-1.5 pr-2 font-mono">{r.topConnection || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono">{r.bottomConnection || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">
                            {r.stepped
                              ? `${r.od1 || "—"} / ${r.od2 || "—"}`
                              : (r.outerDiameter || "—")}
                          </td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.innerDiameter || "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.lengthIn || "—"}</td>
                        </>
                      ) : (
                        <>
                          <td className="py-1.5 pr-2">{r.nominalSize}</td>
                          <td className="py-1.5 pr-2 font-mono">{r.weight}</td>
                          <td className="py-1.5 pr-2">{r.grade}</td>
                          <td className="py-1.5 pr-2">{r.connection}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.outerDiameter}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">{r.innerDiameter}</td>
                          <td className="py-1.5 pr-2 font-mono text-right">
                            {r.internalCapacity?.toFixed(5) ?? "—"}
                          </td>
                          <td className="py-1.5 pr-2 font-mono text-right">
                            {r.externalDisplacement?.toFixed(5) ?? "—"}
                          </td>
                        </>
                      )}
                      <td className="py-1.5 pr-2">
                        <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded"
                              style={{
                                background: r.source === "rsc_seed_v1" ? "rgba(245,180,0,0.12)" : "rgba(120,200,255,0.12)",
                                color: r.source === "rsc_seed_v1" ? "var(--rs-amber)" : "#78c8ff",
                              }}>
                          {SOURCE_LABELS[r.source] || r.source || "—"}
                        </span>
                      </td>
                      <td className="py-1.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button type="button"
                                  data-testid={`reflibs-open-${r.id}`}
                                  onClick={() => navigate(`/reference-libraries/${category}/${encodeURIComponent(r.id)}`, { state: forwardLibraryReturn(location) })}
                                  className="p-1.5 rounded text-[var(--rs-text-3)] hover:text-[var(--rs-amber)]"
                                  title={r.source === "rsc_seed_v1" ? "View seed record" : "Edit"}>
                            {r.source === "rsc_seed_v1" ? <Eye className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                          </button>
                          <button type="button"
                                  data-testid={`reflibs-clone-${r.id}`}
                                  onClick={() => handleClone(r)}
                                  className="p-1.5 rounded text-[var(--rs-text-3)] hover:text-[var(--rs-amber)]"
                                  title="Clone">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                          {r.source !== "rsc_seed_v1" && (
                            <>
                              <button type="button"
                                      data-testid={`reflibs-toggle-active-${r.id}`}
                                      onClick={() => handleToggleActive(r)}
                                      className="p-1.5 rounded text-[var(--rs-text-3)] hover:text-[var(--rs-amber)]"
                                      title={inactive ? "Reactivate" : "Hide from pickers"}>
                                {inactive ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                              </button>
                              <button type="button"
                                      data-testid={`reflibs-delete-${r.id}`}
                                      onClick={() => handleDeleteOrToggle(r)}
                                      className="p-1.5 rounded text-[var(--rs-text-3)] hover:text-[#FCA5A5]"
                                      title="Delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
